import React, { useContext, useMemo } from 'react'
import { useHasReceptor } from 'src/pages/Receptors/receptors.hooks'
import { StandardOpenModalButton } from 'src/components/ModalForms/ModalButtons'
import { AddWorkflowEvidenceModal } from 'src/components/ModalForms/Evidence/AddWorkflowEvidence'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import {
  Control,
  CONTROL_STATUS,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { AuditRecord } from '@trustero/trustero-api-web/lib/audit/audit_pb'
import { Range } from 'react-date-range'
import { StandardButtonSize, StandardButtonVariant } from '../../Buttons'
import { ModalFormId } from '../../../ModalForms'
import { AddEvidenceModal } from '../../../ModalForms/Evidence/AddEvidence/AddEvidenceModal'
import { ReceptorScanSpinner } from '../../../../Throbber/ReceptorScanSpinner'
import { LinkEvidenceToControlModal } from '../../../../pages/Evidence/modals/LinkEvidenceToControlModal'
import { ReceptorConfigContextProvider } from '../../../../pages/Receptors/context/ReceptorConfigContext'
import { ConfigureReceptorForm } from '../../../../pages/Receptors/modals/ConfigureReceptorForm'
import ReceptorScanContext from '../../../../context/ReceptorScanStatus'
import {
  convertSuggestionsToText,
  getCustomSuggestionText,
} from '../../../../components/async/suggestions/suggestions.helpers'
import { DateRangeFilter } from '../../TrusteroDateRange/DateRangeFilter'
import { FlexRow } from '../../Flex'
import { useIsAuditor } from '../../../../context/authContext'
import {
  TabPanelButtonGroup,
  TabPanelNoteHeader,
  TabPanelNoteItem,
} from './styles'

export const EvidenceTabNote = ({
  control,
  currentAudit,
  setDateRange,
  dateRange,
  isActive,
}: {
  control: Control.AsObject
  currentAudit?: AuditRecord
  setDateRange: React.Dispatch<React.SetStateAction<Range>>
  dateRange: Range
  isActive?: boolean
}): JSX.Element => {
  const { receptorScanRunning } = useContext(ReceptorScanContext)

  const { data: HasJiraReceptor } = useHasReceptor('trr-jira-workflow', false)
  const hasJiraWorkflowReceptor = useMemo(
    () => HasJiraReceptor?.getHasReceptor() || false,
    [HasJiraReceptor],
  )

  const { data: HasSnowReceptor } = useHasReceptor(
    'trr-servicenow-workflow',
    false,
  )
  const hasServiceNowWorkflowReceptor = useMemo(
    () => HasSnowReceptor?.getHasReceptor() || false,
    [HasSnowReceptor],
  )

  const isNotApplicable = control.status === CONTROL_STATUS.NOTAPPLICABLE
  const controlId = control.id
  const controlModelId = control.modelId
  const name = `${controlModelId} ${control.name}`
  const suggestedEvidence = control.suggestions?.suggestedEvidence
  const requiredEvidenceDescription = useMemo(() => {
    const suggestionBody = suggestedEvidence?.body || ''
    if (suggestionBody) {
      const suggestionObj = { suggestion: suggestionBody }
      return (
        (suggestedEvidence?.isCustom
          ? getCustomSuggestionText(suggestionObj)
          : convertSuggestionsToText(suggestionObj)) || ''
      ).trim()
    }
    return (control.suggestedEvidence || '').trim()
  }, [control.suggestedEvidence, suggestedEvidence])
  const canAddEvidence = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_ADD_EVIDENCE,
    control,
  )
  const canLinkEvidence = useCanPerformAction<Control.AsObject>(
    CAPABILITY.CAN_LINK_EVIDENCE_CONTROLS,
    control,
  )

  const addEvidenceModalId = isNotApplicable
    ? ModalFormId.NA_INFO
    : ModalFormId.ADD_EVIDENCE

  const addWorkflowEvidenceModalId = isNotApplicable
    ? ModalFormId.NA_INFO
    : ModalFormId.ADD_WORKFLOW_EVIDENCE

  const linkEvidenceModalId = isNotApplicable
    ? ModalFormId.NA_INFO
    : ModalFormId.LINK_EVIDENCE_TO_CONTROL
  const isAuditor = useIsAuditor()

  return (
    <>
      <TabPanelNoteItem>
        <TabPanelNoteHeader>
          <DateRangeFilter
            currentAudit={currentAudit?.getId()}
            dateRange={dateRange}
            setDateRange={setDateRange}
            additionalToolTip={!!currentAudit}
            additionalToolTipBody="Auditors only consider evidence and tests from within the date range of your current audit."
          />
          <TabPanelButtonGroup>
            <FlexRow gap={12}>
              {!isAuditor &&
                (hasJiraWorkflowReceptor || hasServiceNowWorkflowReceptor) && (
                  <StandardOpenModalButton
                    modalId={addWorkflowEvidenceModalId}
                    variant={StandardButtonVariant.SECONDARY}
                    text="Create Workflow Task"
                    size={StandardButtonSize.SMALL}
                  />
                )}
              <StandardOpenModalButton
                modalId={linkEvidenceModalId}
                variant={StandardButtonVariant.SECONDARY}
                text="Link Evidence"
                size={StandardButtonSize.SMALL}
                isDisabled={!canLinkEvidence}
                disabledReason="You do not have permission to link evidence."
              />
              <StandardOpenModalButton
                modalId={addEvidenceModalId}
                text="Add Evidence"
                size={StandardButtonSize.SMALL}
                isDisabled={!canAddEvidence}
                disabledReason="You do not have permission to add evidence."
              />
            </FlexRow>
          </TabPanelButtonGroup>
        </TabPanelNoteHeader>
        <ReceptorScanSpinner show={receptorScanRunning} />
      </TabPanelNoteItem>
      <AddEvidenceModal controlId={controlId} name={name} openOnDrag />
      <LinkEvidenceToControlModal controlId={controlId} controlName={name} />
      {isActive && (
        <>
          <AddWorkflowEvidenceModal
            controlId={controlId}
            name={name}
            description={requiredEvidenceDescription}
            openOnDrag
          />
          <ReceptorConfigContextProvider>
            <ConfigureReceptorForm />
          </ReceptorConfigContextProvider>
        </>
      )}
    </>
  )
}
